import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useMsalAuth } from '../../context/MsalAuthContext';
import { announcementAPI } from '../../services/m365Api';

const ADMIN_EMAIL = 'taiga.akai@tplg.co.jp';
const STORAGE_KEY = 'announcements_last_read_at';

const NotificationBell = () => {
  const { isAuthenticated, user, tokenReady } = useMsalAuth();
  const [announcements, setAnnouncements] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const dropdownRef = useRef(null);

  const isAdmin = user?.email?.toLowerCase() === ADMIN_EMAIL.toLowerCase();

  const getUnreadCount = useCallback(() => {
    const lastRead = localStorage.getItem(STORAGE_KEY);
    if (!lastRead) return announcements.length;
    return announcements.filter((a) => new Date(a.created_at) > new Date(lastRead)).length;
  }, [announcements]);

  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    setUnreadCount(getUnreadCount());
  }, [getUnreadCount]);

  // お知らせを取得
  const fetchAnnouncements = useCallback(async () => {
    if (!isAuthenticated || !tokenReady) return;
    try {
      const data = await announcementAPI.getAnnouncements();
      setAnnouncements(data);
    } catch (err) {
      console.error('Failed to fetch announcements:', err);
    }
  }, [isAuthenticated, tokenReady]);

  useEffect(() => {
    fetchAnnouncements();
  }, [fetchAnnouncements]);

  // 外部クリックで閉じる
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setShowForm(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  const handleToggle = () => {
    const opening = !isOpen;
    setIsOpen(opening);
    if (opening) {
      // 既読にする
      localStorage.setItem(STORAGE_KEY, new Date().toISOString());
      setUnreadCount(0);
    } else {
      setShowForm(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim() || !content.trim()) return;
    setSubmitting(true);
    try {
      await announcementAPI.createAnnouncement(title.trim(), content.trim());
      setTitle('');
      setContent('');
      setShowForm(false);
      await fetchAnnouncements();
    } catch (err) {
      console.error('Failed to create announcement:', err);
      const detail = err.response?.data?.detail || err.message || '不明なエラー';
      alert(`お知らせの作成に失敗しました: ${detail}`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('このお知らせを削除しますか？')) return;
    try {
      await announcementAPI.deleteAnnouncement(id);
      await fetchAnnouncements();
    } catch (err) {
      console.error('Failed to delete announcement:', err);
      const detail = err.response?.data?.detail || err.message || '不明なエラー';
      alert(`削除に失敗しました: ${detail}`);
    }
  };

  const formatDate = (isoString) => {
    const date = new Date(isoString);
    return `${date.getFullYear()}/${String(date.getMonth() + 1).padStart(2, '0')}/${String(date.getDate()).padStart(2, '0')}`;
  };

  if (!isAuthenticated) return null;

  return (
    <div className="notification-bell-wrapper" ref={dropdownRef}>
      <button
        className="notification-bell"
        onClick={handleToggle}
        aria-label="お知らせ"
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <svg viewBox="0 0 24 24" fill="currentColor" width="22" height="22">
          <path d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.9 2 2 2zm6-6v-5c0-3.07-1.63-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.64 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2z" />
        </svg>
        {unreadCount > 0 && (
          <span className="notification-badge">{unreadCount > 9 ? '9+' : unreadCount}</span>
        )}
      </button>

      {isOpen && (
        <div className="notification-dropdown">
          <div className="notification-header">
            <span className="notification-header-title">お知らせ</span>
            {isAdmin && (
              <button
                className="notification-add-btn"
                onClick={() => setShowForm(!showForm)}
                title="お知らせを追加"
              >
                {showForm ? '×' : '＋'}
              </button>
            )}
          </div>

          {isAdmin && showForm && (
            <form className="notification-form" onSubmit={handleSubmit}>
              <input
                type="text"
                placeholder="タイトル"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                maxLength={255}
                required
              />
              <textarea
                placeholder="内容"
                value={content}
                onChange={(e) => setContent(e.target.value)}
                rows={3}
                required
              />
              <button type="submit" disabled={submitting}>
                {submitting ? '送信中...' : '投稿'}
              </button>
            </form>
          )}

          <div className="notification-list">
            {announcements.length === 0 ? (
              <div className="notification-empty">お知らせはありません</div>
            ) : (
              announcements.map((a) => (
                <div key={a.id} className="notification-item">
                  <div className="notification-item-header">
                    <span className="notification-item-title">{a.title}</span>
                    {isAdmin && (
                      <button
                        className="notification-delete-btn"
                        onClick={() => handleDelete(a.id)}
                        title="削除"
                      >
                        ×
                      </button>
                    )}
                  </div>
                  <div className="notification-item-content">{a.content}</div>
                  <div className="notification-item-date">{formatDate(a.created_at)}</div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
